import {
  ArrowGo,
  Cart,
  Check,
  Comment,
  Heart,
  HeartFilled,
  Pack,
  Pin,
  Share,
  Shield,
  StarFilled,
  Truck,
  UserFilled,
} from "@assets"
import { Head, Link, router } from "@inertiajs/react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { useEffect, useState } from "react"
import { toast } from "sonner"

import { AuthButton } from "@/components/auth-button"
import { ContentActionsMenu } from "@/components/content-actions-menu"
import FeedItem from "@/components/feed/feed-item"
import { PageHeader } from "@/components/page-header"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { useFavorites } from "@/hooks/use-favorites"
import AppLayout from "@/layouts/app-layout"
import { cn } from "@/lib/utils"
import {
  businessPath,
  cartItemsPath,
  chatGroupsPath,
  marketplaceProductsPath,
  productPath,
  productsEditPath,
} from "@/routes"

interface Product {
  id: number
  title: string
  description: string
  price: number
  sale_price?: number
  condition: "new" | "like_new" | "good" | "fair" | "poor"
  category: {
    id: number
    name: string
    icon: string
  }
  images: {
    id: number
    url: string
    alt?: string
  }[]
  seller: {
    id: number
    name: string
    avatar_url?: string
    rating: number
    reviews_count: number
    verified: boolean
    location: string
    response_time: string
    joined_date: string
    owner_id?: number
  }
  shipping: {
    available: boolean
    cost: number
    estimated_days: string
  }
  is_favorited: boolean
  favorites_count: number
  is_available: boolean
  created_at: string
}

interface Props {
  product: Product
  related_products: {
    id: number
    title: string
    price: number
    sale_price?: number
    image_url?: string
    condition: string
  }[]
  can_edit: boolean
  seller_reviews: {
    id: number
    rating: number
    comment: string
    created_at: string
    buyer: {
      name: string
      avatar_url?: string
    }
  }[]
}

export default function MarketplaceShow({
  product,
  related_products,
  can_edit,
}: Props) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [cartQty, setCartQty] = useState<number>(0)
  const [cartItemId, setCartItemId] = useState<number | null>(null)

  const {
    isFavorited: getStoreFavorite,
    setFavorite: setStoreFavorite,
    getFavoritesCount: getStoreCount,
    setFavoritesCount: setStoreCount,
  } = useFavorites()

  // Use store value if available, otherwise fallback to props
  const storedFavorite = getStoreFavorite("product", product.id)
  const storedCount = getStoreCount("product", product.id)
  const actualFavorited = storedFavorite ?? product.is_favorited
  const actualFavoritesCount = storedCount ?? product.favorites_count ?? 0

  const handleFavorite = () => {
    const newValue = !actualFavorited
    const newCount = newValue
      ? actualFavoritesCount + 1
      : Math.max(0, actualFavoritesCount - 1)

    // Sync with global store immediately for optimistic update
    setStoreFavorite("product", product.id, newValue)
    setStoreCount("product", product.id, newCount)

    router.post(
      `${productPath(product.id)}/favorite`,
      {},
      {
        preserveScroll: true,
        onError: () => {
          // Revert on error
          setStoreFavorite("product", product.id, !newValue)
          setStoreCount(
            "product",
            product.id,
            newValue ? Math.max(0, newCount - 1) : newCount + 1,
          )
        },
      },
    )
  }

  const handleAddToCart = async () => {
    try {
      const token =
        document
          .querySelector('meta[name="csrf-token"]')
          ?.getAttribute("content") ?? ""
      const res = await fetch(cartItemsPath(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token,
          Accept: "application/json",
        },
        credentials: "same-origin",
        body: JSON.stringify({ product_id: product.id }),
      })
      const data = (await res.json()) as {
        message?: string
        errors?: string[]
        cart_count?: number
      }
      if (!res.ok) throw new Error((data?.errors ?? []).join(", "))
      toast.success(data?.message ?? "Producto agregado al carrito")
      // Notificar al header para actualizar el contador de carrito
      window.dispatchEvent(
        new CustomEvent("cart:updated", {
          detail: { cart_count: data?.cart_count },
        }),
      )
      await fetchCartItem()
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "No se pudo agregar al carrito"
      toast.error(msg)
    }
  }

  const fetchCartItem = async () => {
    try {
      const url = new URL(cartItemsPath(), window.location.origin)
      url.searchParams.set("product_id", String(product.id))
      const res = await fetch(url.toString(), {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
      const data = (await res.json()) as {
        cart_item?: { id: number; quantity: number }
      }
      setCartQty(data?.cart_item?.quantity ?? 0)
      setCartItemId(data?.cart_item?.id ?? null)
    } catch {
      // no-op
    }
  }

  const updateQty = async (newQty: number) => {
    const token =
      document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content") ?? ""
    try {
      if (newQty <= 0) {
        const res = await fetch(`${cartItemsPath()}/${cartItemId ?? 0}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": token,
            "X-HTTP-Method-Override": "DELETE",
            Accept: "application/json",
          },
          credentials: "same-origin",
          body: JSON.stringify({ _method: "delete", product_id: product.id }),
        })
        const delData = (await res.json()) as { cart_count?: number }
        setCartQty(0)
        setCartItemId(null)
        toast.success("Producto eliminado del carrito")
        window.dispatchEvent(
          new CustomEvent("cart:updated", {
            detail: { cart_count: delData?.cart_count },
          }),
        )
        return
      }
      const res = await fetch(`${cartItemsPath()}/${cartItemId ?? 0}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token,
          Accept: "application/json",
        },
        credentials: "same-origin",
        body: JSON.stringify({ quantity: newQty, product_id: product.id }),
      })
      const data = (await res.json()) as {
        errors?: string[]
        cart_item?: { id: number; quantity: number }
        cart_count?: number
        message?: string
      }
      if (!res.ok) throw new Error((data?.errors ?? []).join(", "))
      setCartQty(data?.cart_item?.quantity ?? newQty)
      if (data?.cart_item?.id) setCartItemId(data.cart_item.id)
      toast.success(data?.message ?? "Cantidad actualizada")
      window.dispatchEvent(
        new CustomEvent("cart:updated", {
          detail: { cart_count: data?.cart_count },
        }),
      )
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "No se pudo actualizar la cantidad"
      toast.error(msg)
    }
  }

  useEffect(() => {
    void fetchCartItem()
  }, [product.id])

  const handleContactSeller = () => {
    router.post(
      chatGroupsPath(),
      { business_id: product.seller.id },
      {
        preserveScroll: true,
      },
    )
  }

  const handleDelete = () => {
    if (!confirm("¿Eliminar este producto? Esta acción no se puede deshacer."))
      return
    router.delete(productPath(product.id), {
      preserveScroll: false,
      onSuccess: () => {
        router.visit(marketplaceProductsPath())
      },
    })
  }

  const handleShare = async () => {
    try {
      const url = window.location.href
      const title = product?.title ?? "Producto"
      if (navigator.share) {
        await navigator.share({ title, url })
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url)
      }
    } catch {
      // no-op
    }
  }

  const formatPrice = (price: number) => {
    const formatted = new Intl.NumberFormat("es-ES", {
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(price)
    return `€ ${formatted}`
  }

  const formatDate = (dateString: string, shortMonth = false) => {
    const date = new Date(dateString)

    if (!shortMonth) {
      return date.toLocaleDateString("es-ES", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    }

    const day = date.getDate()

    const short = date.toLocaleDateString("es-ES", {
      month: "short",
      year: "numeric",
    })
    const [rawMonth, rawYear] = short.split(" ")
    const month =
      rawMonth.charAt(0).toUpperCase() + rawMonth.slice(1).replace(/\.*$/, ".")

    return `${day} de ${month} ${rawYear}`
  }

  const getConditionLabel = (condition: string) => {
    const conditions = {
      new: "Nuevo",
      like_new: "Como nuevo",
      good: "Buen estado",
      fair: "Estado aceptable",
      poor: "Necesita reparación",
    }
    return conditions[condition as keyof typeof conditions] || condition
  }

  const images = product.images ?? []
  const hasOffer =
    product.sale_price != null &&
    Number(product.sale_price) < Number(product.price)

  const nextImage = () => {
    if (images.length === 0) return
    setCurrentImageIndex((prev) => (prev + 1) % images.length)
  }

  const prevImage = () => {
    if (images.length === 0) return
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length)
  }

  return (
    <AppLayout
      showEditButton={can_edit}
      showDeleteButton={can_edit}
      showBackButton={false}
      showShareButton={false}
      showFavoriteButton={false}
      onEdit={() => router.visit(productsEditPath(product.id))}
      onDelete={handleDelete}
    >
      <Head title={product?.title ?? "Producto"} />

      <div className="mx-0 max-w-6xl sm:mx-auto md:mx-auto md:space-y-6 md:p-6 lg:mx-auto xl:mx-auto">
        {/* Desktop Navigation */}
        <div className="hidden md:block">
          <PageHeader
            showBackButton={false}
            showEdit={can_edit}
            showDelete={can_edit}
            showShare={false}
            showFavorite={false}
            editHref={productsEditPath(product.id)}
            onDelete={handleDelete}
          />
        </div>
        <div className="px-4">
          <div className="text-primary flex flex-wrap items-center gap-2 text-[13px] font-medium tracking-[0.13px]">
            <button
              onClick={() => window.history.back()}
              className="hover:underline"
            >
              <div className="flex items-center gap-1">
                <ArrowGo className="text-primary size-4 rotate-180 fill-current" />
                Market
              </div>
            </button>
            <span className="text-sm font-normal">•</span>
            <Link
              href={marketplaceProductsPath()}
              onClick={(e) => {
                e.preventDefault()
                router.get(marketplaceProductsPath(), {
                  category_id: product.category.id,
                })
              }}
            >
              {product.category.name}
            </Link>
          </div>

          <div className="flex items-start justify-between gap-2">
            <h1 className="mt-[14px] text-[21px] font-semibold tracking-[0px] break-words sm:text-3xl">
              {product.title}
            </h1>
            <ContentActionsMenu
              contentType="Product"
              contentId={product.id}
              authorId={product.seller?.owner_id ?? 0}
              authorName={product.seller?.name ?? "Vendedor"}
            />
          </div>
          <div className="mt-1 mb-2 flex flex-wrap items-center gap-2">
            <span
              className={cn(
                "items-center rounded-[8px] px-[7px] py-[2px] text-[9.5px] font-medium tracking-[0.57px] text-white",
                product.condition === "new" ? "bg-accent" : "bg-foreground",
              )}
            >
              {getConditionLabel(product.condition).toUpperCase()}
            </span>
            <span className="text-muted-foreground flex-wrap text-[13px] tracking-[0.20px]">
              Publicado {formatDate(product.created_at)}
            </span>
          </div>
          <div className="mb-2 flex flex-wrap items-center gap-[14px]">
            <div className="flex items-center gap-[4px]">
              <AuthButton
                variant="ghost"
                size="icon"
                className="h-[14px] w-[16px]"
                onClick={() => void handleFavorite()}
              >
                {actualFavorited ? (
                  <HeartFilled className="text-destructive size-4.5 w-auto fill-current" />
                ) : (
                  <Heart className="size-4.5 w-auto" />
                )}
                <span className="sr-only">
                  {actualFavorited
                    ? "Quitar de favoritos"
                    : "Agregar a favoritos"}
                </span>
              </AuthButton>
              <span className="text-muted-foreground text-[13px] tracking-[0.20px]">
                {actualFavoritesCount}
              </span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-[20px] w-[20px]"
              onClick={() => void handleShare()}
            >
              <Share className="size-5" />
              <span className="sr-only">Compartir</span>
            </Button>
          </div>
        </div>
        <div className="grid gap-[12px] md:gap-6 lg:grid-cols-2">
          {/* Product Images */}
          <div className="min-w-0 space-y-4">
            <div className="bg-images relative h-[231px] w-full overflow-hidden sm:h-[300px] md:h-[400px] lg:h-[500px]">
              {images.length > 0 && (
                <>
                  <img
                    src={images[currentImageIndex].url}
                    alt={images[currentImageIndex].alt ?? product.title}
                    className="h-full w-full object-cover"
                  />
                  {images.length > 1 && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        className="absolute top-1/2 left-2 -translate-y-1/2 transform bg-white/80"
                        onClick={prevImage}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="absolute top-1/2 right-2 -translate-y-1/2 transform bg-white/80"
                        onClick={nextImage}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 transform rounded bg-black/50 px-2 py-1 text-xs text-white">
                        {currentImageIndex + 1} / {images.length}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>

            {/* Image Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto">
                {images.map((image, index) => (
                  <button
                    key={image.id}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`h-16 w-16 flex-shrink-0 overflow-hidden rounded-md border-2 ${
                      currentImageIndex === index
                        ? "border-primary"
                        : "border-transparent"
                    }`}
                  >
                    <img
                      src={image.url}
                      alt={image.alt ?? product.title}
                      className="h-full w-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Details */}
          <div className="min-w-0">
            <div className="mb-[24px] flex flex-col space-y-[12px] px-[12px]">
              {hasOffer && (
                <div className="flex flex-wrap items-baseline gap-[6px]">
                  <span className="bg-secondary rounded-full px-[6px] py-[2px] text-[10px] font-semibold tracking-[0.57px] text-white">
                    {`${Math.round((1 - Number(product.sale_price ?? product.price) / Number(product.price)) * 100)}% OFF`}
                  </span>
                  <span className="text-chart-1 text-[15px] font-light tracking-[0.15px] line-through">
                    € {Number(product.price).toFixed(2)}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-primary text-[26px] font-bold tracking-[0.26px]">
                  {hasOffer
                    ? formatPrice(product.sale_price!)
                    : formatPrice(product.price)}
                </span>
                <span className="text-[12px] font-normal tracking-[0.06px]">
                  [Info cuotas? medios de pago??]
                </span>
              </div>
              {product.is_available ? (
                cartQty > 0 ? (
                  <div className="flex w-full items-center justify-center gap-2">
                    <Button
                      size="lg"
                      variant="default"
                      onClick={() => {
                        void updateQty(cartQty - 1)
                      }}
                    >
                      -
                    </Button>
                    <div className="min-w-10 text-center text-base">
                      {cartQty}
                    </div>
                    <Button
                      size="lg"
                      variant="default"
                      onClick={() => {
                        void updateQty(cartQty + 1)
                      }}
                    >
                      +
                    </Button>
                  </div>
                ) : (
                  <AuthButton
                    className="h-[49px] w-full rounded-[14px] text-[15px] font-normal tracking-[0.45px]"
                    onClick={() => {
                      void handleAddToCart()
                    }}
                    authAction="register"
                  >
                    <Cart className="size-6 fill-white" />
                    Añadir al carrito
                  </AuthButton>
                )
              ) : (
                <Button
                  disabled
                  className="h-[49px] w-full rounded-[14px] text-[15px] font-normal tracking-[0.45px]"
                  size="lg"
                >
                  Producto no disponible
                </Button>
              )}
              <AuthButton
                variant="outline"
                className="text-primary bg-card h-[49px] w-full items-end rounded-[14px] border-0 text-[15px] font-normal tracking-[0.45px]"
                onClick={handleContactSeller}
                authAction="login"
              >
                <Comment className="fill-primary size-6" />
                Contactar al vendedor
              </AuthButton>
            </div>

            {/* Seller Info */}
            <div className="border-chart-4 w-full border-t-[1px] p-[10px]">
              <div className="mb-[26px] flex flex-wrap items-center gap-2">
                <span className="bg-card rounded-[6px] px-[10px] py-[3px] text-[12px] font-medium tracking-[0.24px]">
                  {getConditionLabel(product.condition)}
                </span>
                <span className="bg-card rounded-[6px] px-[10px] py-[3px] text-[12px] font-medium tracking-[0.24px]">
                  {product.category.name}
                </span>
                {/* <span className="font-medium rounded-[6px] px-[10px] py-[3px] text-[12px] tracking-[0.24px] bg-card">
                  {getConditionLabel(product.condition)}
                </span> */}
              </div>
              <div className="flex min-w-0 items-start gap-3">
                <Link href={businessPath(product.seller.id)}>
                  <Avatar className="bg-card h-[60px] w-[60px]">
                    <AvatarImage src={product.seller.avatar_url} />
                    <AvatarFallback className="bg-card">
                      <UserFilled className="fill-chart-2 size-11" />
                    </AvatarFallback>
                  </Avatar>
                </Link>
                <div>
                  <div className="flex flex-wrap items-center gap-1">
                    <Link
                      href={businessPath(product.seller.id)}
                      className="text-[14px] font-medium tracking-[0px] break-words hover:underline"
                    >
                      {product.seller.name}
                    </Link>
                    {product.seller.verified && (
                      <Check className="fill-primary mb-0.5 size-4.5" />
                    )}
                  </div>
                  <div className="text-muted-foreground flex flex-wrap items-center gap-[7px] text-sm">
                    <div className="flex items-center">
                      <StarFilled className="mr-1 size-5 fill-yellow-400 text-yellow-400" />
                      <span className="text-chart-5 text-[13px] font-bold tracking-[0.20px]">
                        {Number(product.seller.rating ?? 0).toFixed(1)}
                      </span>
                      <span className="text-chart-5 bg-card ml-[3px] rounded-[8px] px-[7px] py-[2px] text-[11px] font-medium tracking-[0.20px]">
                        {product.seller.reviews_count} reseñas
                      </span>
                    </div>
                    <span>•</span>
                    <div className="text-muted-foreground flex items-center text-[13px] font-normal tracking-[0.20px]">
                      <Pin className="fill-muted-foreground mr-1 size-4" />
                      <span>{product.seller.location}</span>
                    </div>
                  </div>
                  <div className="text-chart-5 mt-[13px] text-[13px] font-normal tracking-[0.2px]">
                    • Responde en {product.seller.response_time}
                  </div>
                  <div className="text-chart-5 text-[13px] font-normal tracking-[0.2px]">
                    • Miembro desde{" "}
                    {formatDate(product.seller.joined_date, true)}
                  </div>
                  <Link
                    href={businessPath(product.seller.id)}
                    className="racking-[0.18px] text-chart-3 ml-[4px] flex h-[21px] items-center text-[12px] font-medium"
                  >
                    Ver más productos del vendedor
                    <ArrowGo className="fill-chart-3 ml-1 size-4" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Shipping Info */}
            <div className="border-chart-4 w-full border-t-[1px] p-[20px]">
              <div className="flex items-center gap-[9px]">
                <Truck className="fill-primary bg-card h-[32px] w-[32px] rounded-full p-[4px]" />
                <span className="text-chart-5 text-[14px] font-semibold tracking-[0.14px]">
                  Envío
                </span>
              </div>
              <div className="text-chart-5 ml-[41px] text-[13px] font-normal tracking-[0.20px]">
                {product.shipping.available ? (
                  <div className="space-y-2">
                    <span>
                      Coste de envío:{" "}
                      {product.shipping.cost === 0
                        ? "Gratis"
                        : formatPrice(product.shipping.cost)}
                    </span>
                    <span>
                      Tiempo estimado: {product.shipping.estimated_days}
                    </span>
                  </div>
                ) : (
                  <span>Solo recogida en persona</span>
                )}
              </div>
            </div>

            {/* Trust & Safety */}
            <div className="border-chart-4 w-full border-t-[1px] p-[20px]">
              <div className="space-y-[10px]">
                <div className="flex items-center gap-[9px]">
                  <Shield className="fill-primary bg-card h-[32px] w-[32px] rounded-full p-[4px]" />
                  <span className="text-chart-5 text-[14px] font-semibold tracking-[0.14px]">
                    Compra protegida
                  </span>
                </div>
                <div className="flex items-center gap-[9px]">
                  <Pack className="fill-primary bg-card h-[32px] w-[32px] rounded-full p-[4px]" />
                  <span className="text-chart-5 text-[14px] font-semibold tracking-[0.14px]">
                    Devolución en 7 días
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Product Description */}
        <div className="border-chart-4 w-full border-t-[1px] p-[20px]">
          <span className="text-chart-5 text-[14px] font-semibold tracking-[0.14px]">
            Descripción
          </span>
          <div className="text-chart-5 text-[13px] font-normal tracking-[0.2px]">
            <span className="break-words whitespace-pre-wrap">
              {product.description}
            </span>
          </div>
        </div>

        {/* Related Products */}
        {(related_products?.length ?? 0) > 0 && (
          <div className="border-chart-4 w-full border-t-[1px] pt-[20px]">
            <span className="text-chart-5 p-[20px] text-[14px] font-semibold tracking-[0.14px]">
              Productos similares
            </span>
            <div className="grid grid-cols-2 gap-[12px] px-[12px] pt-[14px] md:grid-cols-4">
              {related_products?.slice(0, 4).map((relatedProduct) => {
                const feedItem = {
                  id: relatedProduct.id,
                  type: "product" as const,
                  source_id: relatedProduct.id,
                  name: relatedProduct.title,
                  title: relatedProduct.title,
                  price: relatedProduct.price,
                  current_price:
                    relatedProduct.sale_price ?? relatedProduct.price,
                  sale_price: relatedProduct.sale_price,
                  condition: relatedProduct.condition,
                  images: relatedProduct.image_url
                    ? [relatedProduct.image_url]
                    : [],
                  business: {
                    id: product.seller.id,
                    name: product.seller.name,
                    verified: product.seller.verified,
                  },
                  created_at: product.created_at,
                }

                return (
                  <FeedItem
                    key={relatedProduct.id}
                    item={feedItem}
                    variant="grid"
                    showActions={false}
                  />
                )
              })}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
